import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ChoiceMagneticButton from "../shared/ChoiceMagneticButton.jsx";
import { scrollToId, MIDDLE_EXPANDED_KEY } from "../shared/utils.js";
import ParadigmShiftSection from "./ParadigmShiftSection.jsx";
import OperationalFilterSection from "./OperationalFilterSection.jsx";
import EconomicsScalingSection from "./EconomicsScalingSection.jsx";
import DeploymentSection from "./DeploymentSection.jsx";

const MIDDLE_TEASER_CHIPS = [
  "9 sec reply · 24/7",
  "Qualifies · logs · books",
  "Live in 72 hours · 0 new hires",
];

const MiddleSectionsCollapsible = () => {
  const [expanded, setExpanded] = useState(() => {
    try {
      return sessionStorage.getItem(MIDDLE_EXPANDED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const handleExpand = () => {
    setExpanded(true);
    try {
      sessionStorage.setItem(MIDDLE_EXPANDED_KEY, "true");
    } catch {
      /* ignore */
    }
    scrollToId("middle-sections");
  };

  const handleCollapse = () => {
    setExpanded(false);
    try {
      sessionStorage.setItem(MIDDLE_EXPANDED_KEY, "false");
    } catch {
      /* ignore */
    }
    scrollToId("middle-sections");
  };

  return (
    <div id="middle-sections" data-testid="middle-sections-collapsible">
      <AnimatePresence initial={false}>
        {!expanded && (
          <motion.section
            key="middle-collapsed"
            className="py-10 sm:py-12 border-b border-white/[0.06] bg-[#050810]"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            data-testid="middle-sections-collapsed"
          >
            <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-5">
                How a digital employee works
              </h2>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-6">
                {MIDDLE_TEASER_CHIPS.map((chip) => (
                  <span
                    key={chip}
                    className="text-xs sm:text-sm font-mono text-gray-400 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03]"
                  >
                    {chip}
                  </span>
                ))}
              </div>
              <ChoiceMagneticButton
                className="bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-500 px-6 py-3 rounded-xl font-semibold text-sm sm:text-base"
                onClick={handleExpand}
                data-testid="middle-sections-expand"
              >
                See how it works
              </ChoiceMagneticButton>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="middle-expanded"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            data-testid="middle-sections-expanded"
          >
            <ParadigmShiftSection />
            <OperationalFilterSection />
            <EconomicsScalingSection />
            <DeploymentSection />
            <div className="py-8 border-b border-white/[0.06] bg-[#050810]">
              <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
                <button
                  type="button"
                  onClick={handleCollapse}
                  className="text-sm font-mono text-gray-500 hover:text-white transition-colors underline underline-offset-4"
                  data-testid="middle-sections-collapse"
                >
                  Show less
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MiddleSectionsCollapsible;
